import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalizedLink } from "./i18n/LocalizedLink";
import "./App.css";
import { parseInfoPageIdFromSearch } from "./content/infoPageLinks";
import {
  applyGrainFixed,
  applyPanelRotationAllowed,
  buildCutListCsv,
  buildExportDraft,
  buildLayout,
  buildOffcutCellLabelDescriptor,
  clone,
  computeOrderSize,
  countSheetStrategyOverrides,
  countStripEligibleSheets,
  CUT_STRATEGIES,
  getDefaultProject,
  getDemoProject,
  emptyPanel,
  emptyRestStockItem,
  emptySubproject,
  filterWerkplanRows,
  getDisplayOffcuts,
  getFlatOrderRows,
  getOffcutMode,
  getPotentialOffcuts,
  getSheetStrategyInstruction,
  getSheetStrategyOverride,
  getWerkplanSheets,
  groupRowsByThickness,
  makeId,
  normalizeExportAnnotations,
  normalizeProject,
  normalizeWerkplan,
  PANEL_THICKNESSES,
  DEFAULT_THICKNESS_PRESETS,
  parseProjectFile,
  PROJECT_COLORS,
  resolveExportRow,
  resolveSheetStrategy,
  shouldShowCutSequence,
  shouldShowOffcuts,
  STRIP_MODE_HELP,
  strategyUsesOffcutDisplay,
} from "@plaatplan/core";
import { getMaterialBaseName, summarizeMaterialPlan } from "./export/materialLabels";
import { downloadText, slugifyProjectName } from "./export/download";
import { ExportBoard } from "./export/ExportBoard";
import { CutSimulator } from "./export/CutSimulator";
import { isMobileAppPromoEnabled, isMobileHandoffEnabled } from "./config/featureFlags";
import { ExportMenu } from "./export/ExportMenu";
import { WorkshopHandoffPanel } from "./export/WorkshopHandoffPanel";
import {
  getSheetPanelId,
  getSheetSummaryId,
  getSheetTabId,
} from "./board/sheetSurfaceIds";
import { BoardView } from "./ui/BoardView";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import { Modal } from "./ui/Modal";
import { useFocusOnMount } from "./ui/useFocusOnMount";
import { handleTabListKeyDown } from "./ui/tabListKeyboard";
import { buildZaagplanDisplaySheets } from "./ui/zaagplanDisplay";
import { ZaagplanEmptyState } from "./ui/ZaagplanEmptyState";
import { buildPlanMetrics } from "./ui/planMetrics";
import { MaterialSummaryView } from "./export/MaterialSummaryView";
import { downloadProjectFile } from "./export/projectFileDownload";
import { WerkplanSawSettings } from "./export/WerkplanSawSettings";
import {
  ExportAlertSection,
  ExportCutListSections,
  ExportMaterialOverview,
  ExportStripDeliverySection,
} from "./export/WerkplanExportSections";
import { WerkplanMaterialScope } from "./export/WerkplanMaterialScope";
import { loadLocalePreferences } from "./i18n/localePreferences";
import { useLocale } from "./i18n/useLocale";
import { SettingsMenuButton } from "./i18n/SettingsMenuButton";
import {
  localizePath,
  resolveAppError,
  resolveCoreMessage,
  resolveCutStrategyLabel,
  resolveSheetPrintHeading,
  resolveSheetPrintSummary,
} from "@plaatplan/i18n";
import { useInfoPages } from "./i18n/useInfoPages";
import { SeoHead } from "./seo/SeoHead";
import { StandaardplaatCard } from "./input/StandaardplaatCard";
import { ThicknessField } from "./input/ThicknessField";
import { LengthInput } from "./input/LengthInput";
import { QuickSubprojectInput } from "./input/QuickSubprojectInput";
import { SubprojectPartInput } from "./input/SubprojectPartInput";
import { SiteFooter } from "./ui/SiteFooter";
import { ConfirmIconButton } from "./ui/ConfirmIconButton";
import { IconButton } from "./ui/IconButton";
import { InfoPageModal } from "./ui/InfoPageModal";
import { PlanCard } from "./ui/PlanCard";
import { LiveResultCard } from "./ui/LiveResultCard";
import { MaterialStockSection } from "./ui/MaterialStockSection";
import { RestStockCard } from "./ui/RestStockCard";
import { DismissibleMobileAppPromo } from "./promo/DismissibleMobileAppPromo";
import { useCompactViewport } from "./promo/useCompactViewport";
import { useMaxWidth } from "./ui/useMediaQuery";
import { PlanSummaryStrip } from "./ui/PlanSummaryStrip";
import { PlaatplanLogo } from "./ui/PlaatplanLogo";
import { SaveIndicator } from "./ui/SaveIndicator";
const STORAGE_KEY = "plaatplan.project";

function loadInitialProject() {
  const locale = loadLocalePreferences().locale;

  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved
      ? normalizeProject(JSON.parse(saved))
      : clone(getDefaultProject(locale));
  } catch {
    return clone(getDefaultProject(locale));
  }
}

function EdgeModal({ panel, projectName, onSave, onClose }) {
  const { display } = useLocale();
  const [edges, setEdges] = useState({
    top: false, right: false, bottom: false, left: false, ...(panel.edges || {}),
  });
  const sideKeys = ["top", "right", "bottom", "left"];

  return (
    <Modal
      title={display.t("web.edgeModal.title")}
      subtitle={`${projectName} · ${panel.name || display.t("web.panels.unnamedPanel")}`}
      onClose={onClose}
    >
      <div className="edge-grid">
        {sideKeys.map((key) => (
          <label className="checkbox-card" key={key}>
            <input
              type="checkbox"
              checked={Boolean(edges[key])}
              onChange={(event) => setEdges({ ...edges, [key]: event.target.checked })}
            />
            <span>{display.t(`web.edgeModal.sides.${key}`)}</span>
          </label>
        ))}
      </div>
      <div className="modal-actions">
        <button type="button" className="secondary-button" onClick={onClose}>
          {display.t("common.cancel")}
        </button>
        <button type="button" className="primary-button" onClick={() => onSave(edges)}>
          {display.t("common.save")}
        </button>
      </div>
    </Modal>
  );
}

function SubprojectTable({
  subproject,
  defaultThickness,
  plateNumbers,
  canRemove,
  onRename,
  onColor,
  onRemove,
  onUpdatePanel,
  onAddPanel,
  onAddParsedPanel,
  onDuplicatePanel,
  onDeletePanel,
  onTogglePanelHidden = () => {},
  onEdges,
}) {
  const { display } = useLocale();
  const dimensionUnit = display.unitSystem === "imperial"
    ? display.t("web.fields.unitIn")
    : display.t("web.fields.unitMm");

  return (
    <section className="section project-section" style={{ "--project-color": subproject.color }}>
      <header className="project-section-heading">
        <span className="project-chip" aria-hidden="true" />
        <input
          className="project-name-input"
          value={subproject.name}
          onChange={(event) => onRename(subproject.id, event.target.value)}
          aria-label={display.t("web.panels.projectNameAria")}
        />
        <div className="project-heading-actions">
          {subproject.panels.some((panel) => panel.hidden) && (
            <span className="hidden-count">
              {display.t("web.panels.hiddenCount", {
                count: subproject.panels.filter((panel) => panel.hidden).length,
              })}
            </span>
          )}
          <label className="color-field" title={display.t("web.panels.colorTitle")}>
            <span className="color-field-label">{display.t("web.panels.color")}</span>
            <input
              type="color"
              value={subproject.color}
              onChange={(event) => onColor(subproject.id, event.target.value)}
              aria-label={display.t("web.panels.colorTitle")}
            />
          </label>
          {canRemove && (
            <button type="button" className="remove-project" onClick={() => onRemove(subproject.id)}>
              {display.t("web.panels.remove")}
            </button>
          )}
        </div>
      </header>
      <div className="table-scroll nested">
        <table className="panel-table">
          <thead>
            <tr>
              <th scope="col"><span className="sr-only">{display.t("web.panels.columns.number")}</span></th>
              <th scope="col">{display.t("web.panels.columns.name")}</th>
              <th scope="col">{display.t("web.panels.columns.length", { unit: dimensionUnit })}</th>
              <th scope="col">{display.t("web.panels.columns.width", { unit: dimensionUnit })}</th>
              <th scope="col">{display.t("web.panels.columns.thickness", { unit: dimensionUnit })}</th>
              <th scope="col">{display.t("web.panels.columns.quantity")}</th>
              <th scope="col">{display.t("web.panels.columns.sheet")}</th>
              <th scope="col">{display.t("web.panels.columns.edges")}</th>
              <th scope="col"><span className="sr-only">{display.t("web.panels.columns.actions")}</span></th>
            </tr>
          </thead>
          <tbody>
            {subproject.panels.map((panel, index) => {
              const edgeCount = Object.values(panel.edges || {}).filter(Boolean).length;
              const plates = plateNumbers[panel.id] || [];
              const isHidden = Boolean(panel.hidden);
              const rowLabel = display.t("web.panels.rowLabel", { number: index + 1 });
              const edgeButtonLabel = edgeCount
                ? display.t(
                  edgeCount === 1
                    ? "web.panels.edgeSidesSingular"
                    : "web.panels.edgeSidesPlural",
                  { count: edgeCount },
                )
                : display.t("web.panels.defineEdges");

              return (
                <tr key={panel.id} className={isHidden ? "panel-row-hidden" : ""}>
                  <td
                    className="number-cell panel-cell-number"
                    data-label={display.t("web.panels.panelNumber", { number: index + 1 })}
                  >
                    <span className="sr-only">{index + 1}</span>
                  </td>
                  <td className="panel-cell-name" data-label={display.t("web.panels.columns.name")}>
                    <input
                      className="table-input name-input"
                      value={panel.name}
                      placeholder={display.t("web.panels.namePlaceholder")}
                      aria-label={display.t("web.panels.aria.name", { row: rowLabel })}
                      onChange={(event) => onUpdatePanel(subproject.id, panel.id, "name", event.target.value)}
                    />
                  </td>
                  <td className="panel-cell-length" data-label={display.t("web.panels.columns.length", { unit: dimensionUnit })}>
                    <LengthInput
                      className="table-input dimension-input"
                      valueMm={panel.length}
                      aria-label={display.t("web.panels.aria.length", { row: rowLabel, unit: dimensionUnit })}
                      onChangeMm={(value) => onUpdatePanel(subproject.id, panel.id, "length", value)}
                    />
                  </td>
                  <td className="panel-cell-width" data-label={display.t("web.panels.columns.width", { unit: dimensionUnit })}>
                    <LengthInput
                      className="table-input dimension-input"
                      valueMm={panel.width}
                      aria-label={display.t("web.panels.aria.width", { row: rowLabel, unit: dimensionUnit })}
                      onChangeMm={(value) => onUpdatePanel(subproject.id, panel.id, "width", value)}
                    />
                  </td>
                  <td className="panel-cell-thickness" data-label={display.t("web.panels.columns.thickness", { unit: dimensionUnit })}>
                    <ThicknessField
                      variant="table"
                      valueMm={panel.thickness ?? defaultThickness}
                      presets={PANEL_THICKNESSES}
                      ariaLabel={display.t("web.panels.aria.thickness", { row: rowLabel, unit: dimensionUnit })}
                      onChangeMm={(value) => onUpdatePanel(subproject.id, panel.id, "thickness", value)}
                    />
                  </td>
                  <td className="panel-cell-quantity" data-label={display.t("web.panels.columns.quantity")}>
                    <input
                      className="table-input quantity-input"
                      type="number"
                      min="1"
                      value={panel.quantity}
                      aria-label={display.t("web.panels.aria.quantity", { row: rowLabel })}
                      onChange={(event) => onUpdatePanel(subproject.id, panel.id, "quantity", event.target.value)}
                    />
                  </td>
                  <td className="plate-cell panel-cell-plate" data-label={display.t("web.panels.columns.sheet")}>
                    {isHidden
                      ? display.t("web.panels.hiddenOnSheet")
                      : plates.length
                        ? plates.join(", ")
                        : "-"}
                  </td>
                  <td className="panel-cell-edges" data-label={display.t("web.panels.columns.edgeFinishing")}>
                    <button className="brown-button edge-button" onClick={() => onEdges(subproject, panel)}>
                      {edgeButtonLabel}
                    </button>
                  </td>
                  <td className="row-actions panel-cell-actions" data-label={display.t("web.panels.columns.actions")}>
                    <IconButton
                      className={`hide-panel-button${isHidden ? " active" : ""}`}
                      label={isHidden
                        ? display.t("web.panels.showInLayout")
                        : display.t("web.panels.hideFromLayout")}
                      onClick={() => onTogglePanelHidden(subproject.id, panel.id)}
                    >
                      {isHidden ? "👁" : "🙈"}
                    </IconButton>
                    <IconButton
                      label={display.t("web.panels.duplicate")}
                      onClick={() => onDuplicatePanel(subproject.id, panel.id)}
                    >
                      ⧉
                    </IconButton>
                    <ConfirmIconButton
                      label={display.t("web.panels.delete")}
                      confirmLabel={display.t("web.panels.deleteConfirm", {
                        name: panel.name || display.t("web.panels.defaultPanelName"),
                      })}
                      onConfirm={() => onDeletePanel(subproject.id, panel.id)}
                    >
                      🗑
                    </ConfirmIconButton>
                  </td>
                </tr>
              );
            })}
            {!subproject.panels.length && (
              <tr><td className="empty-row" colSpan="9">{display.t("web.panels.empty")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <SubprojectPartInput
        projectName={subproject.name || "project"}
        onAddPart={(part) => onAddParsedPanel(subproject.id, part)}
        onAddEmptyPanel={() => onAddPanel(subproject.id)}
      />
    </section>
  );
}


function WerkplanExportView({
  project,
  layout,
  onUpdateWerkplan,
  onUpdateAnnotations,
  onClose,
  onWorkshopHandoff,
  mobileHandoffEnabled,
}) {
  const { display } = useLocale();
  const { tier } = useCompactViewport();
  const mobileAppPromoEnabled = isMobileAppPromoEnabled();
  const backRef = useRef(null);
  const [exportTab, setExportTab] = useState("prepare");
  const [openSimulatorSheetIndex, setOpenSimulatorSheetIndex] = useState(null);
  const pendingPrintRef = useRef(false);
  const rows = getFlatOrderRows(project);
  const exportDate = new Date().toLocaleDateString(display.localeTag);

  const exportDraft = useMemo(
    () => buildExportDraft(project.werkplan, project.exportAnnotations, rows, layout),
    [project.werkplan, project.exportAnnotations, rows, layout],
  );
  const werkplanScope = useMemo(
    () => ({
      includeNewSheets: exportDraft.includeNewSheets !== false,
      includeRestSheets: exportDraft.includeRestSheets !== false,
    }),
    [exportDraft.includeNewSheets, exportDraft.includeRestSheets],
  );
  const exportRows = useMemo(
    () => filterWerkplanRows(rows, layout, werkplanScope),
    [rows, layout, werkplanScope],
  );
  const rowsByThickness = groupRowsByThickness(exportRows);
  const exportSheets = useMemo(
    () => getWerkplanSheets(layout, werkplanScope),
    [layout, werkplanScope],
  );
  const stripEligibleSheetCount = useMemo(
    () => countStripEligibleSheets(layout, project.sourcePanel),
    [layout, project.sourcePanel],
  );
  const strategyOverrideCount = useMemo(
    () => countSheetStrategyOverrides(exportDraft),
    [exportDraft],
  );
  const offcutDisplayRelevant = useMemo(
    () => exportSheets.some((sheet) => strategyUsesOffcutDisplay(
      resolveSheetStrategy(exportDraft, sheet, project.sourcePanel).strategy,
    )),
    [exportDraft, exportSheets, project.sourcePanel],
  );

  const exportRowOptions = useMemo(
    () => ({
      layout,
      sourcePanel: project.sourcePanel,
      scope: werkplanScope,
    }),
    [layout, project.sourcePanel, werkplanScope],
  );

  const resolveRow = (row) => resolveExportRow(
    row,
    exportDraft,
    exportDraft.rowOverrides[row.id] || {},
    exportRowOptions,
  );

  const hasAnyOrderOversize = useMemo(
    () => exportRows.some((row) => resolveExportRow(
      row,
      exportDraft,
      exportDraft.rowOverrides[row.id] || {},
      exportRowOptions,
    ).hasOrderOversize),
    [exportRows, exportDraft, exportRowOptions],
  );

  const updateRowOverride = (rowId, field, value) => {
    if (!["instruction", "orderLength", "orderWidth"].includes(field)) return;

    onUpdateAnnotations((current) => {
      const previous = current.rowOverrides[rowId] || exportDraft.rowOverrides[rowId] || {};
      const baseRow = rows.find((row) => row.id === rowId);

      if (field === "instruction") {
        return {
          ...current,
          rowOverrides: {
            ...current.rowOverrides,
            [rowId]: {
              ...previous,
              instruction: value,
              instructionEdited: true,
            },
          },
        };
      }

      const currentSizes = baseRow
        ? computeOrderSize(baseRow, exportDraft, previous, exportRowOptions)
        : { orderLength: 0, orderWidth: 0 };

      return {
        ...current,
        rowOverrides: {
          ...current.rowOverrides,
          [rowId]: {
            ...previous,
            orderLength: field === "orderLength"
              ? Number(value)
              : Number(previous.orderLength ?? currentSizes.orderLength),
            orderWidth: field === "orderWidth"
              ? Number(value)
              : Number(previous.orderWidth ?? currentSizes.orderWidth),
            orderSizeEdited: true,
          },
        },
      };
    });
  };

  const resetRowInstruction = (rowId) => {
    onUpdateAnnotations((current) => ({
      ...current,
      rowOverrides: {
        ...current.rowOverrides,
        [rowId]: {
          ...(current.rowOverrides[rowId] || exportDraft.rowOverrides[rowId] || {}),
          instruction: "",
          instructionEdited: false,
        },
      },
    }));
  };

  const resetRowOrderSize = (rowId) => {
    onUpdateAnnotations((current) => ({
      ...current,
      rowOverrides: {
        ...current.rowOverrides,
        [rowId]: {
          ...(current.rowOverrides[rowId] || exportDraft.rowOverrides[rowId] || {}),
          orderLength: undefined,
          orderWidth: undefined,
          orderSizeEdited: false,
        },
      },
    }));
  };

  const updateSheetNote = (sheetIndex, value) => {
    onUpdateAnnotations((current) => ({
      ...current,
      sheetNotes: {
        ...current.sheetNotes,
        [sheetIndex]: value,
      },
    }));
  };

  const updateSheetStrategy = (sheetIndex, value) => {
    onUpdateAnnotations((current) => ({
      ...current,
      sheetStrategies: {
        ...current.sheetStrategies,
        [sheetIndex]: value,
      },
    }));

    if (strategyUsesOffcutDisplay(value) && !project.werkplan.showOffcuts) {
      onUpdateWerkplan({ ...project.werkplan, showOffcuts: true });
    }
  };

  const getExportRow = (row) => {
    const override = exportDraft.rowOverrides[row.id] || {};
    return {
      ...resolveRow(row),
      instruction: override.instruction ?? "",
      instructionEdited: override.instructionEdited ?? false,
      orderSizeEdited: override.orderSizeEdited ?? false,
    };
  };

  const schedulePrint = useCallback(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.print();
      });
    });
  }, []);

  const handlePrint = () => {
    if (exportTab !== "shop") {
      pendingPrintRef.current = true;
      setExportTab("shop");
      return;
    }

    schedulePrint();
  };

  const isPrepareTab = exportTab === "prepare";
  const exportTabSelectedIndex = isPrepareTab ? 0 : 1;

  const focusExportTab = (index) => {
    window.requestAnimationFrame(() => {
      document.getElementById(index === 0 ? "export-tab-prepare" : "export-tab-shop")?.focus();
    });
  };

  const handleExportTabSelect = (index) => {
    setExportTab(index === 0 ? "prepare" : "shop");
    focusExportTab(index);
  };

  useEffect(() => {
    if (!pendingPrintRef.current || exportTab !== "shop") {
      return undefined;
    }

    pendingPrintRef.current = false;
    schedulePrint();
  }, [exportTab, schedulePrint]);

  useEffect(() => {
    if (!isPrepareTab) {
      setOpenSimulatorSheetIndex(null);
    }
  }, [isPrepareTab]);

  useFocusOnMount(backRef);

  return (
    <div className="export-page">
      <a className="skip-link" href="#export-main">{display.t("web.shell.skipLink")}</a>
      <div className="export-toolbar no-print">
        <button
          ref={backRef}
          className="secondary-button export-toolbar-back"
          onClick={onClose}
          type="button"
        >
          {display.t("web.werkplan.backToApp")}
        </button>
        <div className="export-toolbar-actions">
          <button className="primary-button" onClick={handlePrint}>
            {display.t("web.werkplan.printPdf")}
          </button>
        </div>
      </div>

      <main id="export-main" tabIndex={-1}>
      <header className="export-header">
        <div>
          <p className="export-kicker">
            {isPrepareTab
              ? display.t("web.werkplan.kickerPrepare")
              : display.t("web.werkplan.kickerShop")}
          </p>
          <h1>{display.t("web.werkplan.title")}</h1>
          <p className="export-subtitle no-print">
            {isPrepareTab
              ? display.t("web.werkplan.subtitlePrepare")
              : display.t("web.werkplan.subtitleShop")}
          </p>
          <p className="export-subtitle print-only">
            {display.t("web.werkplan.subtitlePrint")}
          </p>
          <p className="export-header-mobile-summary no-print">
            <strong>{project.projectName}</strong>
            {" · "}
            {summarizeMaterialPlan(project.materialName, layout.breakdown)}
          </p>
        </div>
        <dl className="export-facts">
          <div><dt>{display.t("web.werkplan.facts.date")}</dt><dd>{exportDate}</dd></div>
          <div><dt>{display.t("web.werkplan.facts.project")}</dt><dd>{project.projectName}</dd></div>
          <div>
            <dt>{display.t("web.werkplan.facts.material")}</dt>
            <dd>{summarizeMaterialPlan(project.materialName, layout.breakdown)}</dd>
          </div>
          <div>
            <dt>{display.t("web.werkplan.facts.standardSheet")}</dt>
            <dd>{display.formatSheet(project.sourcePanel.length, project.sourcePanel.width)}</dd>
          </div>
          <div>
            <dt>{display.t("web.werkplan.facts.kerf")}</dt>
            <dd>{display.formatLength(project.sourcePanel.kerf || 0)}</dd>
          </div>
          <div>
            <dt>{display.t("web.werkplan.facts.margin")}</dt>
            <dd>{display.formatLength(project.sourcePanel.margin || 0)}</dd>
          </div>
          {layout.breakdown.length > 1 && (
            <div>
              <dt>{display.t("web.werkplan.facts.cutLists")}</dt>
              <dd>{display.t("web.werkplan.facts.thicknessCount", { count: layout.breakdown.length })}</dd>
            </div>
          )}
        </dl>
      </header>

      {mobileAppPromoEnabled && isPrepareTab ? (
        <DismissibleMobileAppPromo
          enableHandoff={mobileHandoffEnabled}
          layout={tier === "tablet" ? "tablet" : "phone"}
          onHandoff={onWorkshopHandoff}
          variant="werkplan"
        />
      ) : null}

      <div className="export-tab-shell no-print">
        <div
          className="export-tab-bar"
          role="tablist"
          aria-label={display.t("web.werkplan.tabs.listLabel")}
          onKeyDown={(event) => handleTabListKeyDown(event, {
            count: 2,
            selectedIndex: exportTabSelectedIndex,
            onSelectIndex: handleExportTabSelect,
          })}
        >
          <button
            type="button"
            role="tab"
            id="export-tab-prepare"
            aria-selected={isPrepareTab}
            aria-controls="export-panel-prepare"
            tabIndex={isPrepareTab ? 0 : -1}
            className={`export-tab${isPrepareTab ? " active" : ""}`}
            onClick={() => handleExportTabSelect(0)}
          >
            <span className="export-tab-step" aria-hidden="true">1</span>
            <span className="export-tab-label">{display.t("web.werkplan.tabs.prepare")}</span>
          </button>
          <button
            type="button"
            role="tab"
            id="export-tab-shop"
            aria-selected={!isPrepareTab}
            aria-controls="export-panel-shop"
            tabIndex={!isPrepareTab ? 0 : -1}
            className={`export-tab${!isPrepareTab ? " active" : ""}`}
            onClick={() => handleExportTabSelect(1)}
          >
            <span className="export-tab-step" aria-hidden="true">2</span>
            <span className="export-tab-label">{display.t("web.werkplan.tabs.shop")}</span>
          </button>
        </div>
      </div>

      <div
        id="export-panel-prepare"
        role="tabpanel"
        aria-labelledby="export-tab-prepare"
        className={`export-tab-panel export-tab-panel-prepare${isPrepareTab ? "" : " export-tab-panel-hidden"}`}
      >
        <WerkplanMaterialScope
          werkplan={project.werkplan}
          layout={layout}
          materialName={project.materialName}
          sourcePanel={project.sourcePanel}
          onChange={onUpdateWerkplan}
        />

        <ExportAlertSection
          layout={layout}
          sourcePanel={project.sourcePanel}
          werkplanScope={werkplanScope}
        />

        <WerkplanSawSettings
          werkplan={project.werkplan}
          onChange={onUpdateWerkplan}
          stripEligibleSheetCount={stripEligibleSheetCount}
          totalSheetCount={exportSheets.length}
          strategyOverrideCount={strategyOverrideCount}
          offcutDisplayRelevant={offcutDisplayRelevant}
        />

        <ExportMaterialOverview
          layout={layout}
          project={project}
          werkplanScope={werkplanScope}
        />

        <section className="export-section">
          <h2>{display.t("web.werkplan.visualLayout.title")}</h2>
          <p className="export-note">
            {display.t("web.werkplan.visualLayout.prepareNote")}
          </p>
          <div className="export-board-grid">
            {exportSheets.map((sheet) => {
              const sheetResolution = resolveSheetStrategy(exportDraft, sheet, project.sourcePanel);
              const effectiveStrategy = sheetResolution.strategy;
              const stripAssessment = sheetResolution.assessment;
              const sheetStrategyOverride = getSheetStrategyOverride(exportDraft, sheet.index);
              const offcutMode = getOffcutMode(effectiveStrategy, exportDraft);
              const offcuts = getPotentialOffcuts(
                sheet,
                project.sourcePanel,
                Number(exportDraft.minOffcutLength || 500),
                Number(exportDraft.minOffcutWidth || 300),
                offcutMode,
              );
              const automaticInstruction = getSheetStrategyInstruction(
                effectiveStrategy,
                sheet,
                project.sourcePanel,
                exportDraft,
              );
              const displayOffcuts = getDisplayOffcuts(offcuts, effectiveStrategy);

              return (
                <div key={sheet.index} className="export-board-with-note">
                  <div className="sheet-strategy-panel">
                    <div className="sheet-strategy-header">
                      <h3>{display.t("web.werkplan.visualLayout.sheetStrategyTitle", {
                        number: sheet.index + 1,
                      })}</h3>
                      <label className="no-print">
                        <span>{display.t("web.werkplan.visualLayout.strategyForSheet")}</span>
                        <select
                          value={exportDraft.sheetStrategies[sheet.index] || "inherit"}
                          onChange={(event) => updateSheetStrategy(sheet.index, event.target.value)}
                        >
                          <option value="inherit">
                            {display.t("web.werkplan.visualLayout.inheritStrategy")}
                          </option>
                          {Object.entries(CUT_STRATEGIES).map(([value]) => (
                            <option
                              key={value}
                              value={value}
                              disabled={value === "strips" && !stripAssessment.eligible}
                            >
                              {resolveCutStrategyLabel(display, value)}
                              {value === "strips" && !stripAssessment.eligible
                                ? display.t("web.werkplan.visualLayout.notPossible")
                                : ""}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    {sheetStrategyOverride && (
                      <div className="sheet-strategy-deviation-banner screen-only" role="status">
                        {display.t("web.werkplan.visualLayout.deviationBanner")}{" "}
                        <strong>{resolveCutStrategyLabel(display, sheetStrategyOverride)}</strong>
                      </div>
                    )}
                    {sheetResolution.stripBlocked && (
                      <div className="strip-blocked-notice screen-only" role="status">
                        <strong>{resolveCoreMessage(display, STRIP_MODE_HELP.whenBlocked.title)}</strong>
                        <p>{resolveCoreMessage(display, sheetResolution.assessment.detail)}</p>
                        <p className="strip-blocked-fallback">
                          {display.t("web.werkplan.visualLayout.stripRequested")}{" "}
                          <strong>{resolveCutStrategyLabel(display, "strips")}</strong>
                          {" · "}
                          {display.t("web.werkplan.visualLayout.stripShownAs")}{" "}
                          <strong>{resolveCutStrategyLabel(display, "efficient_cutting")}</strong>
                        </p>
                      </div>
                    )}
                    <p className="screen-only"><strong>{resolveCutStrategyLabel(display, effectiveStrategy)}:</strong> {resolveCoreMessage(display, automaticInstruction)}</p>
                    {(effectiveStrategy === "preserve_offcuts" || effectiveStrategy === "strips") && displayOffcuts.length > 0 && (
                      <ul className="offcut-list screen-only">
                        {displayOffcuts.map((offcut, index) => (
                          <li key={`${offcut.label}-${index}`}>
                            {effectiveStrategy === "strips" && exportDraft.stripsNoCrosscut
                              ? display.t("web.werkplan.visualLayout.offcutStripSelfCut")
                              : display.t("web.werkplan.visualLayout.offcutPotential")}
                            {": "}
                            {display.t("web.werkplan.visualLayout.offcutApprox", {
                              size: display.formatPanelSize(offcut.length, offcut.width),
                              label: resolveCoreMessage(display, buildOffcutCellLabelDescriptor(offcut)),
                            })}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <CutSimulator
                    sourcePanel={project.sourcePanel}
                    sheet={sheet}
                    strategy={effectiveStrategy}
                    exportDraft={exportDraft}
                    isOpen={openSimulatorSheetIndex === sheet.index}
                    onOpen={() => setOpenSimulatorSheetIndex(sheet.index)}
                    onClose={() => setOpenSimulatorSheetIndex(null)}
                    showMobileAppPromo={mobileAppPromoEnabled}
                    enableMobileHandoff={mobileHandoffEnabled}
                    onWorkshopHandoff={onWorkshopHandoff}
                    boardProps={{
                      offcuts: displayOffcuts,
                      showOffcuts: shouldShowOffcuts(effectiveStrategy, exportDraft.showOffcuts),
                      stripsNoCrosscut: exportDraft.stripsNoCrosscut,
                      minOffcutLength: Number(exportDraft.minOffcutLength || 500),
                      minOffcutWidth: Number(exportDraft.minOffcutWidth || 300),
                    }}
                  />
                  <label className="sheet-note-field">
                    <span>{display.t("web.werkplan.visualLayout.sheetNoteLabel", {
                      number: sheet.index + 1,
                    })}</span>
                    <textarea
                      className={(exportDraft.sheetNotes[sheet.index] || "").trim() ? "" : "empty-print-field"}
                      value={exportDraft.sheetNotes[sheet.index] || ""}
                      onChange={(event) => updateSheetNote(sheet.index, event.target.value)}
                      rows={2}
                      placeholder={display.t("web.werkplan.visualLayout.sheetNotePlaceholder")}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </section>

        <ExportCutListSections
          variant="prepare"
          rowsByThickness={rowsByThickness}
          materialName={project.materialName}
          layout={layout}
          werkplanScope={werkplanScope}
          exportDraft={exportDraft}
          hasAnyOrderOversize={hasAnyOrderOversize}
          getExportRow={getExportRow}
          updateRowOverride={updateRowOverride}
          resetRowInstruction={resetRowInstruction}
          resetRowOrderSize={resetRowOrderSize}
        />
      </div>

      <div
        id="export-panel-shop"
        role="tabpanel"
        aria-labelledby="export-tab-shop"
        className={`export-tab-panel export-tab-panel-shop${!isPrepareTab ? "" : " export-tab-panel-hidden"}`}
      >
        <ExportAlertSection
          layout={layout}
          sourcePanel={project.sourcePanel}
          werkplanScope={werkplanScope}
        />

        <section className="export-section export-shop-note no-print">
          <h2>{display.t("web.werkplan.shop.generalInstruction")}</h2>
          <p>{exportDraft.generalNote}</p>
        </section>

        <ExportMaterialOverview
          layout={layout}
          project={project}
          werkplanScope={werkplanScope}
        />

        <ExportCutListSections
          variant="shop"
          rowsByThickness={rowsByThickness}
          materialName={project.materialName}
          layout={layout}
          werkplanScope={werkplanScope}
          exportDraft={exportDraft}
          hasAnyOrderOversize={hasAnyOrderOversize}
          getExportRow={getExportRow}
          updateRowOverride={updateRowOverride}
          resetRowInstruction={resetRowInstruction}
          resetRowOrderSize={resetRowOrderSize}
        />

        <ExportStripDeliverySection
          exportSheets={exportSheets}
          sourcePanel={project.sourcePanel}
          exportDraft={exportDraft}
        />

        <section className="export-section export-footer-note export-shop-controle">
          <h2>{display.t("web.werkplan.shop.control")}</h2>
          <p>{exportDraft.generalNote}</p>
        </section>

        <section className="export-section page-break-before export-shop-layouts">
          <h2>{display.t("web.werkplan.visualLayout.title")}</h2>
          <p className="export-note no-print">
            {display.t("web.werkplan.visualLayout.shopNote")}
          </p>
          <div className="export-board-grid">
            {exportSheets.map((sheet) => {
              const sheetResolution = resolveSheetStrategy(exportDraft, sheet, project.sourcePanel);
              const effectiveStrategy = sheetResolution.strategy;
              const offcutMode = getOffcutMode(effectiveStrategy, exportDraft);
              const offcuts = getPotentialOffcuts(
                sheet,
                project.sourcePanel,
                Number(exportDraft.minOffcutLength || 500),
                Number(exportDraft.minOffcutWidth || 300),
                offcutMode,
              );
              const displayOffcuts = getDisplayOffcuts(offcuts, effectiveStrategy);

              return (
                <div key={sheet.index} className="export-board-with-note">
                  <p className="print-strategy-summary">
                    <strong>{display.t("web.werkplan.visualLayout.sheetPrintPrefix", {
                      number: sheet.index + 1,
                    })}</strong>{" "}
                    {resolveSheetPrintHeading(
                      display,
                      sheet,
                      project.sourcePanel,
                      exportDraft,
                      effectiveStrategy,
                    )}.{" "}
                    {resolveSheetPrintSummary(
                      display,
                      sheet,
                      project.sourcePanel,
                      exportDraft,
                      effectiveStrategy,
                      displayOffcuts,
                    )}
                  </p>
                  <ExportBoard
                    sourcePanel={project.sourcePanel}
                    sheet={sheet}
                    offcuts={displayOffcuts}
                    showOffcuts={shouldShowOffcuts(effectiveStrategy, exportDraft.showOffcuts)}
                    showCutSequence={shouldShowCutSequence(effectiveStrategy, exportDraft.showCutSequence)}
                    strategy={effectiveStrategy}
                    stripsNoCrosscut={exportDraft.stripsNoCrosscut}
                    minOffcutLength={Number(exportDraft.minOffcutLength || 500)}
                    minOffcutWidth={Number(exportDraft.minOffcutWidth || 300)}
                    printLandscape
                  />
                  {(exportDraft.sheetNotes[sheet.index] || "").trim() && (
                    <p className="export-sheet-note-line">
                      <strong>{display.t("web.werkplan.visualLayout.remark")}</strong>{" "}
                      {exportDraft.sheetNotes[sheet.index]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
      </main>
    </div>
  );
}


export default function App() {
  const { display, preferences } = useLocale();
  const infoPages = useInfoPages();
  const [project, setProject] = useState(loadInitialProject);
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [edgeSelection, setEdgeSelection] = useState(null);
  const [message, setMessage] = useState("");
  const [showWerkplanExport, setShowWerkplanExport] = useState(false);
  const [showMaterialSummary, setShowMaterialSummary] = useState(false);
  const [showWorkshopHandoff, setShowWorkshopHandoff] = useState(false);
  const mobileHandoffEnabled = isMobileHandoffEnabled();
  const mobileAppPromoEnabled = isMobileAppPromoEnabled();
  const [lastSavedAt, setLastSavedAt] = useState(() => new Date());
  const [infoPage, setInfoPage] = useState(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const importRef = useRef(null);
  const exportFocusReturnRef = useRef(null);

  useEffect(() => {
    const pageId = parseInfoPageIdFromSearch(window.location.search);
    if (pageId && infoPages[pageId]) {
      setInfoPage(pageId);
    }
  }, [infoPages]);

  const rememberExportFocus = useCallback(() => {
    const active = document.activeElement;
    exportFocusReturnRef.current = active instanceof HTMLElement ? active : null;
  }, []);

  const restoreExportFocus = useCallback(() => {
    const target = exportFocusReturnRef.current;
    exportFocusReturnRef.current = null;
    if (target && typeof target.focus === "function") {
      window.requestAnimationFrame(() => target.focus());
    }
  }, []);

  const openWerkplanExport = useCallback(() => {
    rememberExportFocus();
    setShowWerkplanExport(true);
  }, [rememberExportFocus]);

  const openMaterialSummary = useCallback(() => {
    rememberExportFocus();
    setShowMaterialSummary(true);
  }, [rememberExportFocus]);

  const closeWerkplanExport = useCallback(() => {
    setShowWerkplanExport(false);
    restoreExportFocus();
  }, [restoreExportFocus]);

  const closeMaterialSummary = useCallback(() => {
    setShowMaterialSummary(false);
    restoreExportFocus();
  }, [restoreExportFocus]);

  const layout = useMemo(
    () => buildLayout(project.sourcePanel, project.projects, project.restStock || []),
    [project.sourcePanel, project.projects, project.restStock],
  );
  const displaySheets = useMemo(
    () => buildZaagplanDisplaySheets(layout, project.sourcePanel),
    [layout, project.sourcePanel],
  );
  const selectedSheetTabIndex = useMemo(
    () => Math.max(0, displaySheets.findIndex((sheet) => sheet.index === selectedSheet)),
    [displaySheets, selectedSheet],
  );
  const handleSheetTabSelect = useCallback((index) => {
    const sheet = displaySheets[index];
    if (!sheet) {
      return;
    }

    setSelectedSheet(sheet.index);
    window.requestAnimationFrame(() => {
      document.getElementById(getSheetTabId(sheet))?.focus();
    });
  }, [displaySheets]);
  const planMetrics = useMemo(
    () => buildPlanMetrics(layout, project.projects),
    [layout, project.projects],
  );
  const canExport = planMetrics.activePartCount > 0;
  const isNarrowLayout = useMaxWidth(860);
  const { tier } = useCompactViewport();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(project));
    setLastSavedAt(new Date());
  }, [project]);

  useEffect(() => {
    if (selectedSheet >= displaySheets.length) {
      setSelectedSheet(Math.max(displaySheets.length - 1, 0));
    }
  }, [displaySheets.length, selectedSheet]);

  const updateProjectRoot = (field, value) => setProject((current) => ({ ...current, [field]: value }));
  const updateSource = (field, value) => setProject((current) => ({ ...current, sourcePanel: { ...current.sourcePanel, [field]: value } }));
  const setPanelRotationAllowed = (allowed) => setProject((current) => ({
    ...current,
    sourcePanel: applyPanelRotationAllowed(current.sourcePanel, allowed),
  }));
  const setGrainFixed = (fixed) => setProject((current) => ({
    ...current,
    sourcePanel: applyGrainFixed(current.sourcePanel, fixed),
  }));
  const addRestStockItem = () => setProject((current) => ({
    ...current,
    restStock: [...(current.restStock || []), emptyRestStockItem(current.sourcePanel.defaultThickness)],
  }));
  const updateRestStockItem = (id, field, value) => setProject((current) => ({
    ...current,
    restStock: (current.restStock || []).map((item) => {
      if (item.id !== id) return item;
      if (field === "thickness") return { ...item, thickness: Number(value) };
      if (field === "quantity") return { ...item, quantity: value };
      return { ...item, [field]: value };
    }),
  }));
  const removeRestStockItem = (id) => setProject((current) => ({
    ...current,
    restStock: (current.restStock || []).filter((item) => item.id !== id),
  }));
  const applySheetPreset = (preset) => setProject((current) => ({
    ...current,
    sourcePanel: {
      ...current.sourcePanel,
      length: preset.length,
      width: preset.width,
    },
  }));
  const applyCombinedPreset = (preset) => setProject((current) => {
    const material = preset.materialPreset;
    let sourcePanel = {
      ...current.sourcePanel,
      defaultThickness: material.defaultThickness,
      length: preset.sheetPreset.length,
      width: preset.sheetPreset.width,
    };
    if (material.grainFixed) {
      sourcePanel = applyGrainFixed(sourcePanel, true);
    } else if (material.allowRotation !== undefined) {
      sourcePanel = applyPanelRotationAllowed(sourcePanel, material.allowRotation);
    }
    return {
      ...current,
      materialName: preset.materialName,
      sourcePanel,
    };
  });
  const applyMaterialPreset = (preset) => setProject((current) => {
    let sourcePanel = {
      ...current.sourcePanel,
      defaultThickness: preset.defaultThickness,
    };
    if (preset.grainFixed) {
      sourcePanel = applyGrainFixed(sourcePanel, true);
    } else if (preset.allowRotation !== undefined) {
      sourcePanel = applyPanelRotationAllowed(sourcePanel, preset.allowRotation);
    }
    return {
      ...current,
      materialName: preset.materialName,
      sourcePanel,
    };
  });
  const updateSubproject = (id, fields) => setProject((current) => ({
    ...current,
    projects: current.projects.map((subproject) => subproject.id === id ? { ...subproject, ...fields } : subproject),
  }));
  const updatePanel = (projectId, panelId, field, value) => setProject((current) => ({
    ...current,
    projects: current.projects.map((subproject) =>
      subproject.id !== projectId ? subproject : {
        ...subproject,
        panels: subproject.panels.map((panel) => panel.id === panelId ? { ...panel, [field]: value } : panel),
      }),
  }));

  const addSubproject = (name) => setProject((current) => {
    const trimmed = String(name || "").trim();
    const label = trimmed || display.t("web.subproject.defaultName", {
      number: current.projects.length + 1,
    });
    return {
      ...current,
      projects: [
        ...current.projects,
        emptySubproject(label, PROJECT_COLORS[current.projects.length % PROJECT_COLORS.length]),
      ],
    };
  });
  const removeSubproject = (id) => setProject((current) => ({ ...current, projects: current.projects.filter((item) => item.id !== id) }));
  const addPanel = (projectId) => setProject((current) => ({
    ...current,
    projects: current.projects.map((subproject) => subproject.id === projectId ? {
      ...subproject,
      panels: [...subproject.panels, emptyPanel(current.sourcePanel.defaultThickness)],
    } : subproject),
  }));
  const addParsedPanel = (projectId, part) => setProject((current) => ({
    ...current,
    projects: current.projects.map((subproject) => subproject.id === projectId ? {
      ...subproject,
      panels: [...subproject.panels, {
        ...emptyPanel(current.sourcePanel.defaultThickness),
        name: part.name,
        length: part.length,
        width: part.width,
        quantity: part.quantity,
      }],
    } : subproject),
  }));
  const duplicatePanel = (projectId, panelId) => setProject((current) => ({
    ...current,
    projects: current.projects.map((subproject) => {
      if (subproject.id !== projectId) return subproject;
      const original = subproject.panels.find((panel) => panel.id === panelId);
      if (!original) return subproject;
      return {
        ...subproject,
        panels: [...subproject.panels, {
          ...clone(original),
          id: makeId(),
          hidden: false,
          name: `${original.name || display.t("web.panels.panelDefaultName")} ${display.t("web.panels.copySuffix")}`,
        }],
      };
    }),
  }));
  const deletePanel = (projectId, panelId) => setProject((current) => ({
    ...current,
    projects: current.projects.map((subproject) => subproject.id !== projectId ? subproject : {
      ...subproject, panels: subproject.panels.filter((panel) => panel.id !== panelId),
    }),
  }));

  const togglePanelHidden = (projectId, panelId) => setProject((current) => ({
    ...current,
    projects: current.projects.map((subproject) => subproject.id !== projectId ? subproject : {
      ...subproject,
      panels: subproject.panels.map((panel) => panel.id === panelId ? { ...panel, hidden: !panel.hidden } : panel),
    }),
  }));

  const downloadCutListCsv = () => {
    const rows = getFlatOrderRows(project);
    const exportDraft = buildExportDraft(project.werkplan, project.exportAnnotations, rows, layout);
    const csv = buildCutListCsv(rows, layout, exportDraft, project.sourcePanel, {
      resolveMessage: (descriptor) => resolveCoreMessage(display, descriptor),
    });
    downloadText(
      `${slugifyProjectName(project.projectName)}-zaaglijst.csv`,
      csv,
      "text/csv;charset=utf-8",
    );
    setMessage(display.t("web.messages.csvDownloaded"));
  };

  const exportProjectBackup = () => {
    downloadProjectFile(project, layout);
    setMessage(display.t("web.messages.projectDownloaded"));
  };

  const importProject = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const { project: imported } = parseProjectFile(await file.text());
      setProject(normalizeProject(imported));
      setSelectedSheet(0);
      setMessage(display.t("web.messages.importSuccess"));
    } catch (error) {
      setMessage(display.t("web.messages.importFailed", {
        message: resolveAppError(display, error),
      }));
    } finally {
      event.target.value = "";
    }
  };

  const loadDemo = () => {
    const demo = getDemoProject(preferences.locale);
    setProject(clone(demo));
    setSelectedSheet(0);
    setMessage(display.t("web.messages.demoLoaded", {
      names: demo.projects.map((subproject) => subproject.name).join(", "),
    }));
  };

  const reset = () => {
    setProject(clone(getDefaultProject(preferences.locale)));
    setSelectedSheet(0);
    setMessage(display.t("web.messages.resetStarted"));
  };

  const resetWithConfirm = () => {
    const defaultProject = getDefaultProject(preferences.locale);
    const hasWork = planMetrics.activePartCount > 0
      || (project.restStock || []).length > 0
      || project.projectName !== defaultProject.projectName;

    if (hasWork) {
      setConfirmResetOpen(true);
      return;
    }

    reset();
  };

  if (showMaterialSummary) {
    return (
      <MaterialSummaryView
        project={project}
        layout={layout}
        onClose={closeMaterialSummary}
      />
    );
  }

  if (showWerkplanExport) {
    return (
      <>
        <WerkplanExportView
          project={project}
          layout={layout}
          onUpdateWerkplan={(werkplan) => setProject((current) => ({
            ...current,
            werkplan: normalizeWerkplan(werkplan),
          }))}
          onUpdateAnnotations={(updater) => setProject((current) => ({
            ...current,
            exportAnnotations: normalizeExportAnnotations(updater(current.exportAnnotations || {})),
          }))}
          onClose={closeWerkplanExport}
          onWorkshopHandoff={() => setShowWorkshopHandoff(true)}
          mobileHandoffEnabled={mobileHandoffEnabled}
        />
        {mobileHandoffEnabled ? (
          <WorkshopHandoffPanel
            onClose={() => setShowWorkshopHandoff(false)}
            open={showWorkshopHandoff}
            project={project}
          />
        ) : null}
      </>
    );
  }

  return (
    <div className="page">
      <SeoHead
        faq={infoPages.faq.items}
        path={localizePath("/", preferences.locale)}
        breadcrumb={[{ name: "Plaatplan", path: localizePath("/", preferences.locale) }]}
      />
      <a className="skip-link" href="#main-content">{display.t("web.shell.skipLink")}</a>
      <h1 className="sr-only">{display.t("web.shell.srTitle")}</h1>
      <header className="app-header">
        <div className="brand">
          <LocalizedLink to="/" className="brand-link" aria-label={display.t("web.siteHeader.brandAria")}>
            <PlaatplanLogo className="brand-logo" size={38} />
          </LocalizedLink>
          <div>
            <p className="eyebrow">{display.t("web.shell.eyebrow")}</p>
            <LocalizedLink to="/" className="brand-name brand-name-link">Plaatplan</LocalizedLink>
          </div>
        </div>
        <div className="header-actions">
          <SaveIndicator savedAt={lastSavedAt} />
          <div className="header-action-divider" aria-hidden="true" />
          <SettingsMenuButton />
          <div className="header-action-divider" aria-hidden="true" />
          <div className="header-action-group">
            <button
              type="button"
              className="ghost-button"
              onClick={() => importRef.current?.click()}
            >
              {display.t("web.actions.import")}
            </button>
            <ExportMenu
              canExport={canExport}
              enableMobileHandoff={mobileHandoffEnabled}
              onPrintPdf={openWerkplanExport}
              onMaterialSummary={openMaterialSummary}
              onCutListCsv={downloadCutListCsv}
              onProjectBackup={exportProjectBackup}
              onWorkshopHandoff={() => setShowWorkshopHandoff(true)}
              onOpenChange={(isOpen) => {
                if (isOpen) setMessage("");
              }}
            />
          </div>
          <input ref={importRef} hidden type="file" accept=".json,application/json" onChange={importProject} />
        </div>
      </header>

      {message && (
        <div className="notice" role="status">
          <span>{message}</span>
          <button onClick={() => setMessage("")} aria-label={display.t("web.shell.closeNotice")}>×</button>
        </div>
      )}

      <div className="workspace">
        <aside className="control-rail">
          <PlanCard
            projectName={project.projectName}
            onProjectNameChange={(value) => updateProjectRoot("projectName", value)}
            onReset={resetWithConfirm}
          />
          <LiveResultCard layout={layout} />

          {!isNarrowLayout && (
            <MaterialStockSection collapsed={false} materialBaseName={getMaterialBaseName(project.materialName)}>
              <StandaardplaatCard
                project={project}
                panelThicknesses={PANEL_THICKNESSES}
                defaultThicknessPresets={DEFAULT_THICKNESS_PRESETS}
                onUpdateSource={updateSource}
                onApplyCombinedPreset={applyCombinedPreset}
                onApplyMaterialPreset={applyMaterialPreset}
                onSetPanelRotationAllowed={setPanelRotationAllowed}
                onSetGrainFixed={setGrainFixed}
                onApplySheetPreset={applySheetPreset}
              />
              <RestStockCard
                restStock={project.restStock || []}
                panelThicknesses={PANEL_THICKNESSES}
                materialName={project.materialName}
                onAdd={addRestStockItem}
                onUpdate={updateRestStockItem}
                onRemove={removeRestStockItem}
              />
            </MaterialStockSection>
          )}
        </aside>

        <main className="work-area" id="main-content" tabIndex={-1}>
          <section className="canvas-card visual-section layout-block-zaagplan">
            <PlanSummaryStrip layout={layout} projects={project.projects} />
            {mobileAppPromoEnabled ? (
              <DismissibleMobileAppPromo
                enableHandoff={mobileHandoffEnabled}
                layout={tier === "tablet" ? "tablet" : "phone"}
                onHandoff={() => setShowWorkshopHandoff(true)}
                variant="planner"
              />
            ) : null}
            <div className="canvas-header">
              <div>
                <p className="card-kicker">{display.t("web.sections.optimization")}</p>
                <h2>{display.t("web.sections.layout")}</h2>
              </div>
              <div className="canvas-header-side">
                <div className="legend">
                  {project.projects.map((subproject) => (
                    <span key={subproject.id} className="legend-item">
                      <i style={{ background: subproject.color }} />
                      {subproject.name || display.t("web.panels.unnamedProject")}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {displaySheets.length > 0 ? (
              <>
                <nav
                  className="sheet-tabs"
                  role="tablist"
                  aria-label={display.t("web.sheetTab.listLabel")}
                  onKeyDown={(event) => handleTabListKeyDown(event, {
                    count: displaySheets.length,
                    selectedIndex: selectedSheetTabIndex,
                    onSelectIndex: handleSheetTabSelect,
                  })}
                >
                  {displaySheets.map((sheet, sheetTabIndex) => {
                    const tabId = getSheetTabId(sheet);
                    const panelId = getSheetPanelId(sheet);
                    const isSelected = sheet.index === selectedSheet;

                    return (
                      <button
                        key={sheet.unused ? `unused-${sheet.restStockId}-${sheet.restStockOccurrence}` : sheet.index}
                        id={tabId}
                        type="button"
                        role="tab"
                        aria-selected={isSelected}
                        aria-controls={panelId}
                        tabIndex={sheetTabIndex === selectedSheetTabIndex ? 0 : -1}
                        className={[
                          isSelected ? "selected" : "",
                          sheet.source === "rest" ? "sheet-tab--rest" : "",
                          sheet.unused ? "sheet-tab--unused" : "",
                        ].filter(Boolean).join(" ")}
                        onClick={() => handleSheetTabSelect(sheetTabIndex)}
                      >
                        <strong>{sheet.unused ? "-" : sheet.index + 1}</strong>
                        <span>
                          {sheet.unused
                            ? display.t("web.sheetTab.unused")
                            : display.t(sheet.source === "rest" ? "web.sheetTab.rest" : "web.sheetTab.sheet")}
                        </span>
                        <small>{display.formatThickness(sheet.thickness)}</small>
                      </button>
                    );
                  })}
                </nav>
                {displaySheets.map((sheet) => (
                  <BoardView
                    key={sheet.unused ? `unused-${sheet.restStockId}-${sheet.restStockOccurrence}` : sheet.index}
                    sourcePanel={project.sourcePanel}
                    sheet={sheet}
                    visible={sheet.index === selectedSheet}
                    tabId={getSheetTabId(sheet)}
                    panelId={getSheetPanelId(sheet)}
                    summaryId={getSheetSummaryId(sheet)}
                  />
                ))}
                <p className="footnote">{display.t("web.layout.footnote")}</p>
              </>
            ) : (
              <ZaagplanEmptyState onLoadDemo={loadDemo} />
            )}
          </section>

          <div className="projects-toolbar layout-block-deelprojecten">
            <div>
              <p className="card-kicker">{display.t("web.sections.partsList")}</p>
              <h2>{display.t("web.sections.subprojects")}</h2>
            </div>
          </div>

          <QuickSubprojectInput className="layout-block-deelprojecten" onAdd={addSubproject} />

          <div className="project-stack layout-block-deelprojecten">
            {project.projects.map((subproject) => (
              <SubprojectTable
                key={subproject.id}
                subproject={subproject}
                defaultThickness={project.sourcePanel.defaultThickness}
                plateNumbers={layout.plateNumbersByPanelId}
                canRemove={project.projects.length > 1}
                onRename={(id, name) => updateSubproject(id, { name })}
                onColor={(id, color) => updateSubproject(id, { color })}
                onRemove={removeSubproject}
                onUpdatePanel={updatePanel}
                onAddPanel={addPanel}
                onAddParsedPanel={addParsedPanel}
                onDuplicatePanel={duplicatePanel}
                onDeletePanel={deletePanel}
                onTogglePanelHidden={togglePanelHidden}
                onEdges={(selectedProject, panel) => setEdgeSelection({ project: selectedProject, panel })}
              />
            ))}
          </div>
        </main>

        {isNarrowLayout && (
          <MaterialStockSection collapsed materialBaseName={getMaterialBaseName(project.materialName)}>
            <StandaardplaatCard
              project={project}
              panelThicknesses={PANEL_THICKNESSES}
              defaultThicknessPresets={DEFAULT_THICKNESS_PRESETS}
              onUpdateSource={updateSource}
              onApplyCombinedPreset={applyCombinedPreset}
              onApplyMaterialPreset={applyMaterialPreset}
              onSetPanelRotationAllowed={setPanelRotationAllowed}
              onSetGrainFixed={setGrainFixed}
              onApplySheetPreset={applySheetPreset}
            />
            <RestStockCard
              restStock={project.restStock || []}
              panelThicknesses={PANEL_THICKNESSES}
              materialName={project.materialName}
              onAdd={addRestStockItem}
              onUpdate={updateRestStockItem}
              onRemove={removeRestStockItem}
            />
          </MaterialStockSection>
        )}
      </div>

      <SiteFooter onOpenPage={setInfoPage} showSeoIntro />

      {edgeSelection && (
        <EdgeModal
          panel={edgeSelection.panel}
          projectName={edgeSelection.project.name}
          onClose={() => setEdgeSelection(null)}
          onSave={(edges) => {
            updatePanel(edgeSelection.project.id, edgeSelection.panel.id, "edges", edges);
            setEdgeSelection(null);
          }}
        />
      )}

      {infoPage && (
        <InfoPageModal
          pageId={infoPage}
          page={infoPages[infoPage]}
          onClose={() => setInfoPage(null)}
        />
      )}

      {confirmResetOpen && (
        <ConfirmDialog
          title={display.t("web.confirm.reset.title")}
          message={display.t("web.confirm.reset.message")}
          confirmLabel={display.t("web.confirm.reset.confirmLabel")}
          onConfirm={() => {
            setConfirmResetOpen(false);
            reset();
          }}
          onCancel={() => setConfirmResetOpen(false)}
        />
      )}

      {mobileHandoffEnabled ? (
        <WorkshopHandoffPanel
          onClose={() => setShowWorkshopHandoff(false)}
          open={showWorkshopHandoff}
          project={project}
        />
      ) : null}
    </div>
  );
}
